import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Lightbulb, BookOpen, Image as ImageIcon } from "lucide-react";
import { HELP_SECTIONS, searchHelp } from "@/lib/helpContent";

function Block({ block }) {
  if (block.t === "p") return <p className="text-sm text-slate-600 leading-relaxed">{block.c}</p>;
  if (block.t === "ul")
    return (
      <ul className="space-y-1.5">
        {block.c.map((li) => (
          <li key={li} className="flex gap-2 text-sm text-slate-600 leading-relaxed">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
            <span>{li}</span>
          </li>
        ))}
      </ul>
    );
  if (block.t === "steps")
    return (
      <ol className="space-y-2">
        {block.c.map((li, i) => (
          <li key={li} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed">
            <span className="shrink-0 h-5 w-5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
            <span className="pt-0.5">{li}</span>
          </li>
        ))}
      </ol>
    );
  if (block.t === "tip")
    return (
      <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5">
        <Lightbulb className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-[13px] text-amber-800 leading-relaxed">{block.c}</p>
      </div>
    );
  return null;
}

export default function HelpDialog({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(HELP_SECTIONS[0].id);

  const results = useMemo(() => searchHelp(HELP_SECTIONS, query), [query]);
  const active = results.find((s) => s.id === activeId) || results[0] || null;
  const ActiveIcon = active?.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[85vh] p-0 gap-0 overflow-hidden flex flex-col" data-testid="help-dialog">
        <DialogHeader className="px-5 py-4 border-b border-slate-100">
          <DialogTitle className="font-display flex items-center gap-2 text-slate-900">
            <BookOpen className="h-5 w-5 text-blue-600" /> Help & User Guide
          </DialogTitle>
          <DialogDescription className="text-xs">Everything you need to run production planning — search or browse the topics below.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col sm:flex-row">
          {/* Sidebar: search + topic list */}
          <aside className="sm:w-64 shrink-0 border-b sm:border-b-0 sm:border-r border-slate-100 flex flex-col min-h-0">
            <div className="p-3">
              <div className="relative">
                <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search help…"
                  className="pl-9 h-9"
                  data-testid="help-search-input"
                />
              </div>
            </div>
            <nav className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5" data-testid="help-nav">
              {results.length === 0 && (
                <p className="text-xs text-slate-400 italic px-2 py-3" data-testid="help-no-results">No topics match “{query}”.</p>
              )}
              {results.map((s) => {
                const Icon = s.icon;
                const isActive = active?.id === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveId(s.id)}
                    data-testid={`help-nav-${s.id}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                      isActive ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-blue-600" : "text-slate-400"}`} />
                    <span className="truncate">{s.title}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Content pane */}
          <section className="flex-1 min-h-0 overflow-y-auto px-6 py-5" data-testid="help-content">
            {active ? (
              <div className="space-y-4" data-testid={`help-section-${active.id}`}>
                <h3 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
                  {ActiveIcon && <ActiveIcon className="h-5 w-5 text-blue-600" />} {active.title}
                </h3>
                {active.image && (
                  <figure className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50 shadow-sm" data-testid={`help-image-${active.id}`}>
                    <img src={active.image.src} alt={active.image.label} loading="lazy" className="w-full block" />
                    <figcaption className="text-[11px] text-slate-500 px-3 py-2 border-t border-slate-100 flex items-center gap-1.5">
                      <ImageIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" /> {active.image.label}
                    </figcaption>
                  </figure>
                )}
                <div className="space-y-4">
                  {active.blocks.map((b, i) => <Block key={`${b.t}-${i}`} block={b} />)}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic">Select a topic to read more.</p>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
